import React, { useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  RadioButton,
  Select,
  TextField,
  Typography,
} from '../../../../../components/atoms';
import {
  LocationNumber as LocationNumberIcon,
  OrderCollection as OrderCollectionIcon,
  OrderTableService as OrderTableServiceIcon,
} from '../../../../../components/icons';
import {
  CollectionLocationArea,
  PickupLocationOption,
  PickupLocationType,
  TableServiceCategory,
} from '../../../../../core/api/restaurant/restaurant.types';
import * as Actions from '../../../../../weborder/actions/Actions';
import * as Store from '../../../../../weborder/Store';
import { checkoutUtils, ServiceTypes } from '../../../utils/checkoutUtils';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OuterProps = {
  handleNumber: (e: any) => any;
  locationAreas: CollectionLocationArea[];
  pickupLocationOptions: PickupLocationOption[];
  serviceTypes: ServiceTypes;
  tableNumber: number | undefined;
  tableServiceCategory: number;
  toggleTableDialog: () => void;
  vrId: number;
};
type Props = OuterProps & StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  locationSelector: {
    marginBottom: theme.spacing(2),

    '& .MuiSelect-selectMenu, .MuiSelect-select': {
      padding: theme.spacing(1.625, 5.75, 1.625),
    },

    '&.MuiInputBase-root': {
      border: `1px solid ${theme.palette.grey[400]}`,
      position: 'relative',

      '&.Mui-focused': {
        border: `1px solid ${theme.palette.common.black}`,
      },
      '& .MuiInputAdornment-positionStart, .MuiInputAdornment-positionEnd': {
        position: 'absolute',
      },
      '& .MuiInputAdornment-positionStart': {
        left: theme.spacing(1.5),
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.common.black,
        right: theme.spacing(1),
      },
    },
  },

  row: {
    marginBottom: theme.spacing(2),
  },

  radioGroup: {
    marginBottom: theme.spacing(4.5),
    padding: 0,
  },

  groupItem: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    borderTop: '1px solid transparent',
    display: 'flex',
    padding: theme.spacing(2, 1.5),

    '&:first-of-type': {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },

    '&:hover': {
      borderColor: 'transparent',
      boxShadow: `0 3px 0 -2px ${theme.palette.primary.main}, 0 -3px 0 -2px ${theme.palette.primary.main}`,
      cursor: 'pointer',
    },
  },

  orderOption: {
    width: '100%',
  },

  subText: {
    fontSize: theme.typography.pxToRem(14),
  },

  itemLabel: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',

    '& .fd-icon': {
      marginRight: theme.spacing(1.5),
    },
  },
}));

export function OrderTypeOptions(props: Props): JSX.Element {
  const {
    editPickupLocationTypes,
    handleNumber,
    locationAreas,
    pickupLocationOptions,
    serviceTypes,
    tableNumber,
    tableServiceCategory,
    toggleTableDialog,
    translate,
    vrId,
  } = props;

  const {
    isDineInAndTakeOut,
    isDriveThrough,
    isTableServiceAndTakeOut,
    isTableServiceOnly,
  } = serviceTypes;

  const classes = useStyles();

  const [orderType, setOrderType] = useState(0);

  const hasTableListOptions =
    !_isEmpty(pickupLocationOptions) || !_isEmpty(locationAreas);

  const getButtonText = (): string => {
    switch (tableServiceCategory) {
      case TableServiceCategory.Gate:
        return translate('Gate_delivery') as string;
      case TableServiceCategory.DriveThrough:
        return translate('Drive_Through') as string;
      default:
        return translate('Dine_in') as string;
    }
  };

  const getButtonSubtext = (): string => {
    if (isDriveThrough) {
      return translate('Pick_up_at_drive_through') as string;
    }

    if (typeof tableNumber !== 'number') {
      return translate(`Select_table_${tableServiceCategory}`) as string;
    }

    if (!_isEmpty(locationAreas)) {
      let tableName = '';
      locationAreas.forEach((area) => {
        const match = area.Locations.find(
          (table) => table.LocationId === tableNumber,
        );
        if (match) tableName = match.LocationName;
      });
      if (tableName) return tableName;
      return `${translate(`Table_${tableServiceCategory}`)} ${tableNumber}`;
    }
    if (!_isEmpty(pickupLocationOptions)) {
      return (
        pickupLocationOptions.find(
          (option) => option.PickupLocationOptionId === tableNumber,
        ).Value ||
        `${translate(`Table_${tableServiceCategory}`)} ${tableNumber}`
      );
    }

    return `${translate(`Table_${tableServiceCategory}`)} ${tableNumber}`;
  };

  const handleChangeOption = (index, type): void => {
    setOrderType(index);
    editPickupLocationTypes(vrId, type);
  };

  const renderRadioButtonGroup = (radioButtons): JSX.Element => (
    <ul className={classes.radioGroup}>
      {radioButtons.map((config) => (
        <li className={classes.groupItem} key={config.testId}>
          <RadioButton
            checked={config.checked}
            className={classes.orderOption}
            label={config.label}
            onChange={config.onChange}
            testId={config?.testId}
          />
        </li>
      ))}
    </ul>
  );

  if (isTableServiceOnly) {
    if (!hasTableListOptions) {
      return (
        <TextField
          autoFocus
          className={classes.row}
          id="selectLocation"
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          label={translate(`Table_number_${tableServiceCategory}`) as string}
          onChange={handleNumber}
          placeholder={
            translate(`Table_number_${tableServiceCategory}`) as string
          }
          value={tableNumber || ''}
        />
      );
    }

    const tableListOptions = checkoutUtils.getTableListOptions(
      locationAreas,
      pickupLocationOptions,
    );

    tableListOptions.unshift({
      disabled: true,
      label: `${translate('Select_an_option')}`,
      key: 'default',
    });

    const tableNumberOrDefault = tableNumber || 'default';
    const selectedOption = tableListOptions.find(
      (option) => option.key === tableNumberOrDefault.toString(),
    );
    const selectedOrDefaultOption = selectedOption || null;

    const locationSelectorStyles = clsx(classes.locationSelector, classes.row);

    return (
      <Select
        aria-label={
          translate(`Select_table_number_${tableServiceCategory}`) as string
        }
        className={locationSelectorStyles}
        fullWidth
        id="selectLocation"
        key="selectLocation"
        label={translate(`Table_number_${tableServiceCategory}`) as string}
        menuItems={tableListOptions}
        onChange={handleNumber}
        startIcon={<LocationNumberIcon />}
        selected={selectedOrDefaultOption}
      />
    );
  }

  if (isDineInAndTakeOut) {
    const takeOutlabel = (
      <div className={classes.itemLabel}>
        <OrderCollectionIcon />

        <div>
          <Typography>{translate('Take_out')}</Typography>
          <Typography className={classes.subText} variant="body2">
            {translate('Collect_counter')}
          </Typography>
        </div>
      </div>
    );

    const dineInLabel = (
      <div className={classes.itemLabel}>
        <OrderTableServiceIcon />

        <div>
          <Typography>{getButtonText()}</Typography>
          <Typography
            data-fd="selected-dine-in-location"
            className={classes.subText}
            variant="body2"
          >
            {getButtonSubtext()}
          </Typography>
        </div>
      </div>
    );

    const radioButtons = [
      {
        checked: orderType === 0,
        label: takeOutlabel,
        onChange: () => handleChangeOption(0, PickupLocationType.TakeOut),
        testId: 'order-type-take-out',
      },
      {
        checked: orderType === 1,
        label: dineInLabel,
        onChange: () => handleChangeOption(1, PickupLocationType.DineIn),
        testId: 'order-type-dine-in',
      },
    ];

    return renderRadioButtonGroup(radioButtons);
  }

  if (isTableServiceAndTakeOut) {
    const handleTableServiceOption = (): void => {
      toggleTableDialog();
      handleChangeOption(1, PickupLocationType.TableService);
    };

    const takeOutlabel = (
      <div className={classes.itemLabel}>
        <OrderCollectionIcon />

        <div>
          <Typography>{translate('Take_out')}</Typography>
          <Typography className={classes.subText} variant="body2">
            {translate('Collect_counter')}
          </Typography>
        </div>
      </div>
    );

    const tableServiceLabel = (
      <div className={classes.itemLabel}>
        <OrderTableServiceIcon />

        <div>
          <Typography>{getButtonText()}</Typography>
          <Typography
            data-fd="selected-dine-in-location"
            className={classes.subText}
            variant="body2"
          >
            {getButtonSubtext()}
          </Typography>
        </div>
      </div>
    );

    const radioButtons = [
      {
        checked: orderType === 0,
        label: takeOutlabel,
        onChange: () => handleChangeOption(0, PickupLocationType.TakeOut),
        testId: 'order-type-take-out',
      },
      {
        checked: orderType === 1,
        label: tableServiceLabel,
        onChange: handleTableServiceOption,
        testId: 'order-type-table-service',
      },
    ];

    return renderRadioButtonGroup(radioButtons);
  }

  return null;
}

const mapStateToProps = (state: WebOrder.State) => ({
  translate: getTranslate(state.locale),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<Store.State, {}, Action>,
) => ({
  editPickupLocationTypes: (vrid: number, value: PickupLocationType) => {
    dispatch(Actions.EditPickupLocationTypes(vrid, value));
  },
});

const ConnectedOrderTypeOptions = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderTypeOptions);
export { ConnectedOrderTypeOptions };
